import { useState } from "react";
import { Tooltip, message } from "antd";
import {
  DesktopOutlined,
  StopOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { eventTracker } from "../../../utils/eventTracker";
import {
  checkBrowserPushSubscription,
  isSecureNotificationContext,
  sendTestBrowserNotification,
} from "../../../utils/browserNotifications";
import "./BrowserPushToggle.scss";

/**
 * Header control: subscribe / test OS (browser Notification API) toasts.
 * Success feedback is the desktop notification only — no antd toast.
 */
export default function BrowserPushToggle({
  permission,
  supported,
  onEnable,
  onOpenForceModal,
  onRefresh,
}) {
  const [testing, setTesting] = useState(false);
  const secure = isSecureNotificationContext();

  // Still render on insecure HTTP so users can see why it fails.
  if (!supported && secure) return null;

  const granted = permission === "granted";
  const denied = permission === "denied";

  const title = !secure
    ? "Njoftimet kërkojnë HTTPS — hap https://portal1.flex.al"
    : granted
      ? "Testo njoftimin në desktop (OS)"
      : denied
        ? "Njoftimet e bllokuara — kliko për udhëzime"
        : "Aktivizo njoftimet e shfletuesit (OS)";

  const handleClick = async () => {
    eventTracker("BrowserPush", "click", granted ? "Test push" : "Subscribe");

    if (!secure) {
      message.warning(
        "Hape aplikacionin me HTTPS (https://portal1.flex.al) për njoftime OS"
      );
      const httpsUrl =
        "https://" +
        window.location.host +
        window.location.pathname +
        window.location.search +
        window.location.hash;
      window.location.assign(httpsUrl);
      return;
    }

    if (denied) {
      onOpenForceModal?.();
      return;
    }

    if (!granted) {
      const next = await onEnable?.();
      onRefresh?.();
      if (next === "granted") {
        // Proof is the OS / browser-tray notification only (no in-app toast).
        setTimeout(async () => {
          const result = await sendTestBrowserNotification();
          if (!result.ok) onOpenForceModal?.();
        }, 350);
      } else {
        onOpenForceModal?.();
      }
      return;
    }

    setTesting(true);
    try {
      const status = checkBrowserPushSubscription();
      if (!status.subscribed) {
        onRefresh?.();
        onOpenForceModal?.();
        message.warning("Abonimi nuk është aktiv — aktivizo njoftimet");
        return;
      }
      const result = await sendTestBrowserNotification();
      if (!result.ok) {
        message.error("Testi dështoi — kontrollo lejet e shfletuesit");
        onOpenForceModal?.();
      }
      // Success → OS / tray notification only (no antd toast).
    } finally {
      setTesting(false);
    }
  };

  return (
    <div
      className={`browser-push-toggle ${
        granted ? "is-on" : denied ? "is-denied" : "is-off"
      } ${testing ? "is-testing" : ""}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={title}
    >
      <Tooltip placement="bottom" title={title}>
        <span className="browser-push-toggle__stack">
          {denied ? (
            <StopOutlined className="browser-push-toggle__icon" />
          ) : (
            <DesktopOutlined className="browser-push-toggle__icon" />
          )}
          {granted ? (
            <span className="browser-push-toggle__badge" aria-hidden>
              <CheckOutlined />
            </span>
          ) : (
            <span className="browser-push-toggle__dot" />
          )}
        </span>
      </Tooltip>
    </div>
  );
}
